import React, { useState, useMemo } from "react";
import ElectricityDemandFromDB  from "../dashboard/ElectricityDemandFromDB";
import ElectricityExplainerCard from "../dashboard/ElectricityExplainerCard";
import ElectricityYearsContextTable from "../dashboard/ElectricityYearsContextTable";
import CarbonIntensityChart from "../dashboard/CarbonIntensityChart";
import PowerGenerationSystemSize from "../dashboard/PowerGenerationSystemSize";
import PowerGenerationLandUsageChart from "../dashboard/PowerGenerationLandUsageChart";
import RenewableInfrastructureTable from "../dashboard/RenewableInfrastructureTable";

// --- helpers to align comparison keys with whatever family/suffix your main key uses
const parseFamily = (key) => {
  const hasDVL = key?.includes("_dvl_");
  const base = hasDVL ? "net_zero_futures_dvl" : "net_zero_futures";
  const suffix = key?.match(/_(\d{4})$/)?.[0] ?? ""; // e.g. "_2208" | "_0509"
  return { base, suffix };
};
const buildKey = (base, name, ambition, suffix) => `${base}_${name}_${ambition}${suffix}`;

export default function Power({
  powerGenerationContext,
  currentContexts,
  selectedContexts,
  carbonIntensityData,
  carbonIntensityOptions,
  carbonIntensityDetails,        // { intensityReductionPercentage, baselineIntensity, finalIntensity }
  currentScenarioInfo,           // { scenario, variant }
  safePowerGenerationNeeds,
  scenarioKey,                   // primary backend scenario key, e.g. "net_zero_futures_dvl_shift_low_2208"
}) {
  const { base, suffix } = useMemo(() => parseFamily(scenarioKey), [scenarioKey]);
  const baselineScenarioKey = useMemo(() => buildKey(base, "current", "low", suffix), [base, suffix]);

  // --- local comparison state
  const [selectedComparisonValue, setSelectedComparisonValue] = useState("");
  const comparisonScenarioKey = useMemo(() => {
    if (!selectedComparisonValue) return null;
    const [name, ambition = "low"] = selectedComparisonValue.split("-");
    return buildKey(base, name, ambition, suffix);
  }, [selectedComparisonValue, base, suffix]);

  const handleChangeComparison = (value) => setSelectedComparisonValue(value);

  // Optional: a friendly label to show in children that support it
  const comparisonLabel = selectedComparisonValue
    ? selectedComparisonValue.replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())
    : undefined;

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-3">Clean Electricity Supply</h2>
        <p className="text-sm text-gray-600 mb-4 max-w-2xl mx-auto">
          How much clean electricity will be needed to meet the level of demand in your scenario, and what does that mean for building new infrastructure?
        </p>
        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100 inline-block max-w-md mx-auto">
          <p className="text-xs mt-1">
            The UK will need to produce a lot more clean electricity in all scenarios. Transport, heating, and industry will be increasingly powered by electricity instead of fossil fuels.
          </p>
        </div>
      </div>

      <div className="bg-gray-50 p-5 rounded-lg mb-6">
        <div className="mb-4 mt-12">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            How UK electricity demand is projected to change up to 2050
          </h3>
        </div>

        <ElectricityDemandFromDB
          scenario={scenarioKey}                  // primary
          baselineScenario={baselineScenarioKey}  // BAU-ish baseline derived from scenarioKey
          // Pass both names so the child can pick the one it supports
          comparisonScenarioKey={comparisonScenarioKey || undefined}
          comparisonScenario={comparisonScenarioKey || undefined}
          comparisonLabel={comparisonLabel}
          from={2025}
          to={2050}
        />

        {/* Comparison Scenario Dropdown */}
        <div className="mb-6 flex flex-col items-center gap-3">
          <label className="text-xs text-gray-600">Add additional scenario: </label>
          <select
            value={selectedComparisonValue}
            onChange={(e) => handleChangeComparison(e.target.value)}
            className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">None</option>
            {/* <option value="current-low">Current</option> */}
                <option value="steer-low">Less change + Steer</option>
                <option value="shift-low">Less change + Shift</option>
                <option value="transform-low">Less change + Transform</option>
                <option value="steer-high">More change + Steer</option>
                <option value="shift-high">More change + Shift</option>
                <option value="transform-high">More change + Transform</option>
          </select>
        </div>

        {/* Explainer – now suffix-aware to match your dataset */}
        <div className="mt-6 p-3 rounded-lg">
          <ElectricityExplainerCard scenario={buildKey(base, "shift", "high", suffix)} />

          {/* Context table */}
          <div className="bg-white w-4/5 m-auto rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <ElectricityYearsContextTable
                selectedScenario={scenarioKey}          // user’s chosen scenario @2050
                // baselineScenario={baselineScenarioKey}  // fixed/derived baseline @2025
              />
            </div>
          </div>
        </div>
      </div>

      {/* Carbon Intensity */}
      <div className="mb-4 mt-24">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Clean energy impact of your selected scenario</h3>
        <p className="text-m text-gray-600">
          This chart shows the amount of greenhouse gases produced for every unit of electricity generated. This is called carbon intensity.
        </p>
      </div>

      <div className="mt-4 p-4 rounded-lg">
        <div className="w-full">
          <CarbonIntensityChart
            scenario={scenarioKey}
            // If your CarbonIntensityChart supports overlays, uncomment next two:
            // comparisonScenarioKey={comparisonScenarioKey || undefined}
            // comparisonScenario={comparisonScenarioKey || undefined}
            from={2025}
            to={2050}
          />
        </div>
      </div>

      {/* System growth */}
      <div className="mb-4 mt-24">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">What this means for the size of the electricity system</h3>
        <p className="text-m text-gray-600">Projected need for electricity supply from different sources</p>
      </div>

      <div className="mt-6">
        <PowerGenerationSystemSize
          scenario={scenarioKey}
        //   comparisonScenarioKey={comparisonScenarioKey || undefined}
        //   comparisonScenario={comparisonScenarioKey || undefined}
        //   comparisonLabel={comparisonLabel}
          from={2025}
          to={2050}
          // gwScale={1/1000} // uncomment if TAB_ELC-Cap PV is in MW
        />
      </div>

      {/* <div className="space-y-8 mt-6">
        <PowerGenerationLandUsageChart
          scenario={scenarioKey}
        //   comparisonScenarioKey={comparisonScenarioKey || undefined}
        //   comparisonScenario={comparisonScenarioKey || undefined}
        //   comparisonLabel={comparisonLabel}
          from={2025}
          to={2050}
          // gwScale={1/1000} // uncomment if ELC_CAP values are MW
        />
      </div> */}

      <div className="space-y-8 mt-6">
        <RenewableInfrastructureTable
          scenario={scenarioKey}
        //   comparisonScenarioKey={comparisonScenarioKey || undefined}
        //   comparisonScenario={comparisonScenarioKey || undefined}
          from={2050}
          to={2050}
        />
      </div>
    </div>
  );
}
